import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function VideoCard({ clip, index, onOpen }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);

  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  return (
    <motion.button
      data-testid={`highlights-card-${index}`}
      type="button"
      onClick={() => onOpen(clip)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: EASE }}
      className="group relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0D111A] text-left transition-all duration-500 hover:border-[#00F562]/50 hover:shadow-[0_20px_60px_rgba(0,245,98,0.15)]"
    >
      <video
        ref={videoRef}
        src={clip.src}
        poster={clip.poster}
        preload="metadata"
        muted
        playsInline
        loop
        onLoadedData={() => setReady(true)}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
          ready ? "opacity-100" : "opacity-0"
        }`}
      />
      {!ready && (
        <img
          src={clip.poster}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#06080C] via-[#06080C]/30 to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center opacity-90 transition-transform duration-500 group-hover:scale-110">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#00F562]/90 text-[#06080C] shadow-[0_0_40px_rgba(0,245,98,0.55)] backdrop-blur">
          <Play size={22} fill="currentColor" />
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00F562]">
            {clip.tag}
          </span>
          <p className="mt-1 font-display text-xl font-extrabold uppercase leading-tight tracking-wide text-white">
            {clip.title}
          </p>
        </div>
        <span className="font-mono text-xs text-slate-400">
          0{index + 1}
        </span>
      </div>
    </motion.button>
  );
}

export default function VideoShowcase({ t }) {
  const [openClip, setOpenClip] = useState(null);
  const dialogVideoRef = useRef(null);

  const closeModal = () => {
    if (dialogVideoRef.current) {
      dialogVideoRef.current.pause();
    }
    setOpenClip(null);
  };

  return (
    <section
      id="highlights"
      data-testid="highlights-section"
      className="relative overflow-hidden py-24 lg:py-32"
    >
      <div className="absolute left-1/2 top-1/3 h-[40vh] w-[70vw] -translate-x-1/2 rounded-full bg-[#00F562]/[0.04] blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 max-w-3xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#00F562]">
            {t.highlights.overline}
          </p>
          <h2
            data-testid="highlights-title"
            className="mt-5 font-display text-4xl font-black uppercase leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {t.highlights.title}
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
            {t.highlights.body}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.highlights.clips.map((clip, i) => (
            <VideoCard
              key={clip.src}
              clip={clip}
              index={i}
              onOpen={setOpenClip}
            />
          ))}
        </div>

        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
          {t.highlights.disclaimer}
        </p>
      </div>

      <AnimatePresence>
        {openClip && (
          <motion.div
            data-testid="highlights-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-6 backdrop-blur-md"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-[#00F562]/25 bg-[#06080C] shadow-[0_40px_120px_rgba(0,0,0,0.65)]"
            >
              <button
                data-testid="highlights-modal-close"
                onClick={closeModal}
                aria-label="Chiudi video"
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white transition-all duration-300 hover:border-[#00F562]/60 hover:text-[#00F562]"
              >
                <X size={18} />
              </button>
              <video
                ref={dialogVideoRef}
                src={openClip.src}
                poster={openClip.poster}
                controls
                autoPlay
                playsInline
                className="block h-auto w-full"
              />
              <div className="flex items-center justify-between border-t border-white/10 px-6 py-4">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00F562]">
                    {openClip.tag}
                  </span>
                  <p className="mt-1 font-display text-lg font-extrabold uppercase tracking-wide text-white">
                    {openClip.title}
                  </p>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  Talent Hub // Demo
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
